import React, { useState } from 'react';
import { API_BASE } from '../utils/fileUtils';
import '../styles/global.css';

interface NotificationFormProps {
  onNotificationCreated?: () => void;
}

const NotificationForm: React.FC<NotificationFormProps> = ({ onNotificationCreated }) => {
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    notification_type: 'info' as 'profile' | 'info',
    user_id: null as number | null
  });
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.message.trim()) {
      setError('Заполните все поля');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Создаем FormData для отправки файлов
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('message', formData.message);
      submitData.append('notification_type', formData.notification_type);
      if (formData.user_id) {
        submitData.append('user_id', formData.user_id.toString());
      }

      // Добавляем прикрепленные файлы
      attachedFiles.forEach((file, index) => {
        submitData.append(`files`, file);
      });

      const response = await fetch(`${API_BASE}/dashboard/notifications`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: submitData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Ошибка создания уведомления');
      }

      await response.json();
      setSuccess('Уведомление успешно создано');
      setFormData({
        title: '',
        message: '',
        notification_type: 'info',
        user_id: null
      });
      setAttachedFiles([]);

      if (onNotificationCreated) {
        onNotificationCreated();
      }

      setTimeout(() => {
        setSuccess('');
      }, 3000);

    } catch (err: any) {
      setError(err.message || 'Ошибка при создании уведомления');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setAttachedFiles(Array.from(files));
    }
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="notification-form">
      <div className="card">
        <h3 style={{ color: 'var(--primary-orange)', marginBottom: '1.5rem' }}>
          Создать системное уведомление
        </h3>

        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Тип уведомления:</label>
            <select
              className="form-control"
              name="notification_type"
              value={formData.notification_type}
              onChange={handleInputChange}
              required
            >
              <option value="info">Информационное сообщение</option>
              <option value="profile">Профиль сотрудника</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Заголовок:</label>
            <input
              type="text"
              className="form-control"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Введите заголовок уведомления"
              maxLength={100}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Сообщение:</label>
            <textarea
              className="form-control"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Введите текст сообщения"
              rows={4}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Прикрепить файлы:</label>
            <input
              type="file"
              className="form-control"
              multiple
              onChange={handleFileChange}
              accept="*/*"
            />
            {attachedFiles.length > 0 && (
              <div className="mt-2">
                <small className="text-muted">Прикрепленные файлы:</small>
                {attachedFiles.map((file, index) => (
                  <div key={index} className="d-flex justify-content-between align-items-center mt-1">
                    <small>{file.name} ({(file.size / 1024).toFixed(2)} KB)</small>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeFile(index)}
                    >
                      Удалить
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="d-flex gap-2 justify-content-end mt-3">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setFormData({
                  title: '',
                  message: '',
                  notification_type: 'info',
                  user_id: null
                });
                setAttachedFiles([]);
              }}
              disabled={loading}
            >
              Очистить
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Создание...' : 'Создать уведомление'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NotificationForm;